import { initializeApp, FirebaseApp } from "firebase/app";
import { getAuth, Auth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, Firestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, Functions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, FirebaseStorage, connectStorageEmulator } from "firebase/storage";
import { getAnalytics, Analytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { getMessaging, Messaging } from "firebase/messaging";
import { AppCheck, initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

// Log all VITE_ env variables available to the client at runtime for debugging
// console.log("[frontend/src/config/firebase.ts] import.meta.env:", import.meta.env);

const isDevelopment = import.meta.env.MODE === "development";
const looksLikePlaceholder = (value: string | undefined): boolean => {
  if (!value) return true;
  const normalized = value.trim().toLowerCase();
  return normalized === "" || normalized.includes("your-firebase-project-id") || normalized === "your-project-id";
};

const rawApiKey = import.meta.env.VITE_FIREBASE_API_KEY as string | undefined;
const rawProjectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined;
const hasCoreFirebaseConfig = Boolean(!looksLikePlaceholder(rawApiKey) && !looksLikePlaceholder(rawProjectId));

// In local dev, allow sane fallback values so Auth/Firestore emulators can run
// even if env vars are not injected in the client bundle yet.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || (isDevelopment ? "demo-api-key" : undefined),
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || (isDevelopment ? "localhost" : undefined),
  projectId: hasCoreFirebaseConfig ? rawProjectId : (isDevelopment ? "demo-lendlifted" : rawProjectId),
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: looksLikePlaceholder(import.meta.env.VITE_FIREBASE_APP_ID as string | undefined)
    ? (isDevelopment ? "1:000000000000:web:demo" : undefined)
    : import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

if (!hasCoreFirebaseConfig) {
  const errorMessage = "Firebase API Key or Project ID is missing in the client-side bundle. " +
    "Ensure VITE_FIREBASE_API_KEY and VITE_FIREBASE_PROJECT_ID were correctly set in your .env.development file AT BUILD TIME.";
  console.error(errorMessage);
}

const app: FirebaseApp = initializeApp(firebaseConfig);
let appCheck: AppCheck | null = null;

// App Check defaults to enabled in production unless explicitly disabled.
const enableAppCheckInDev = import.meta.env.VITE_ENABLE_APPCHECK_DEV === "true";
const enableAppCheckInProd = import.meta.env.VITE_ENABLE_APPCHECK !== "false";
const shouldInitializeAppCheck = isDevelopment ? enableAppCheckInDev : enableAppCheckInProd;
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

if (shouldInitializeAppCheck && recaptchaSiteKey) {
  if (isDevelopment) {
    const debugToken = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;
    if (debugToken) {
      (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
      console.log("[AppCheck] Using configured debug token from environment.");
    } else {
      (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      console.log("[AppCheck] No debug token found in env. Generated ephemeral token (check console).");
    }
  }

  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true
    });
  } catch (error) {
    console.error("[AppCheck] Initialization failed. Continuing without App Check token attachment.", error);
  }
} else if (shouldInitializeAppCheck && !recaptchaSiteKey) {
  console.warn("[AppCheck] Skipped: VITE_RECAPTCHA_SITE_KEY is missing.");
} else if (!shouldInitializeAppCheck) {
  console.warn("[AppCheck] Disabled. Set VITE_ENABLE_APPCHECK=true (prod) or VITE_ENABLE_APPCHECK_DEV=true (dev) to enable.");
}

const auth: Auth = getAuth(app);
const db: Firestore = getFirestore(app);
const functionsClient: Functions = getFunctions(app, import.meta.env.VITE_FIREBASE_REGION);
const storage: FirebaseStorage = getStorage(app);
let analytics: Analytics | null = null;
let messaging: Messaging | null = null;

// Initialize Analytics only if supported and measurementId is present
(async () => {
  if (firebaseConfig.measurementId && (await isAnalyticsSupported())) {
    try {
      analytics = getAnalytics(app);
    } catch (error) {
      console.error("[frontend] Firebase Analytics could not be initialized:", error);
      analytics = null;
    }
  } else {
    analytics = null;
  }
})();

// Initialize Firebase Cloud Messaging
try {
  messaging = getMessaging(app);
} catch (error) {
  console.error("[frontend] Firebase Messaging could not be initialized:", error);
  messaging = null;
}

if (isDevelopment) {
  console.log("[frontend] Development mode: Connecting to Firebase emulators...");

  const authEmulatorHost = import.meta.env.VITE_EMULATOR_AUTH_HOST || "localhost";
  const authEmulatorPort = parseInt(import.meta.env.VITE_EMULATOR_AUTH_PORT || "9099", 10);
  try {
    connectAuthEmulator(auth, `http://${authEmulatorHost}:${authEmulatorPort}`, { disableWarnings: true });
  } catch (error) {
    console.error(`[CLIENT] Error connecting to Auth emulator:`, error);
  }

  const firestoreEmulatorHost = import.meta.env.VITE_EMULATOR_FIRESTORE_HOST || "localhost";
  const firestoreEmulatorPort = parseInt(import.meta.env.VITE_EMULATOR_FIRESTORE_PORT || "8080", 10);
  try {
    connectFirestoreEmulator(db, firestoreEmulatorHost, firestoreEmulatorPort);
  } catch (error) {
    console.error(`[CLIENT] Error connecting to Firestore emulator:`, error);
  }

  const functionsEmulatorHost = import.meta.env.VITE_EMULATOR_FUNCTIONS_HOST || "localhost";
  const configuredFunctionsPort = import.meta.env.VITE_EMULATOR_FUNCTIONS_PORT;
  const parsedFunctionsPort = parseInt(configuredFunctionsPort || "5000", 10);
  // firebase.json uses port 5000 for functions. If legacy env still has 5001,
  // normalize to 5000 so callable functions work without manual env cleanup.
  const functionsEmulatorPort = parsedFunctionsPort === 5001 ? 5000 : parsedFunctionsPort;
  if (parsedFunctionsPort === 5001) {
    console.warn("[frontend] VITE_EMULATOR_FUNCTIONS_PORT=5001 detected; overriding to 5000 to match firebase.json emulators.functions.port.");
  }
  try {
    connectFunctionsEmulator(functionsClient, functionsEmulatorHost, functionsEmulatorPort);
  } catch (error) {
    console.error(`[CLIENT] Error connecting to Functions emulator:`, error);
  }

  const storageEmulatorHost = import.meta.env.VITE_EMULATOR_STORAGE_HOST || "localhost";
  const storageEmulatorPort = parseInt(import.meta.env.VITE_EMULATOR_STORAGE_PORT || "9199", 10);
  try {
    connectStorageEmulator(storage, storageEmulatorHost, storageEmulatorPort);
  } catch (error) {
    console.error(`[CLIENT] Error connecting to Storage emulator:`, error);
  }
}

export { app, appCheck, auth, db, functionsClient as functions, storage, analytics, messaging };
